import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Icons } from '../lib/Icons';

const HelpDashboardPage = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', name: 'System Overview', IconComponent: Icons.Home },
    { id: 'features', name: 'Key Features', IconComponent: Icons.Sparkles },
    { id: 'navigation', name: 'Navigation Guide', IconComponent: Icons.Compass },
    { id: 'getting-started', name: 'Getting Started', IconComponent: Icons.Rocket }
  ];

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center"><Icons.Home className="w-8 h-8 mr-3" /> SEN Curriculum Management System</h1>
              <p className="text-gray-600 mt-2">
                Complete guide to managing special educational needs curricula and student progress
              </p>
            </div>
            <Link
              to="/dashboard"
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white border-b border-gray-200 rounded-lg shadow-sm mb-8">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.IconComponent className="w-5 h-5 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'overview' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">System Overview</h2>
                <p className="text-gray-600 mb-4">
                  The SEN Curriculum Management System is a comprehensive platform designed specifically for 
                  Special Educational Needs Coordinators (SENCOs) and educational staff. It provides tools 
                  for managing student profiles, generating personalized curricula, tracking progress, and 
                  implementing targeted interventions.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Users className="w-8 h-8 text-blue-600" /></div>
                    <h3 className="font-semibold text-gray-900">Student Management</h3>
                    <p className="text-sm text-gray-600 mt-2">Comprehensive student profiles and records</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.BookOpen className="w-8 h-8 text-green-600" /></div>
                    <h3 className="font-semibold text-gray-900">Curriculum Generation</h3>
                    <p className="text-sm text-gray-600 mt-2">AI-powered personalized curriculum creation</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.ChartBar className="w-8 h-8 text-purple-600" /></div>
                    <h3 className="font-semibold text-gray-900">Progress Tracking</h3>
                    <p className="text-sm text-gray-600 mt-2">Monitor student development and achievements</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="flex justify-center mb-2"><Icons.Target className="w-8 h-8 text-orange-600" /></div>
                    <h3 className="font-semibold text-gray-900">Intervention System</h3>
                    <p className="text-sm text-gray-600 mt-2">Targeted support and progress monitoring</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'features' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Key Features</h2>
                
                <div className="space-y-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Users className="w-5 h-5 mr-2" /> Student Management</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Comprehensive student profiles with SEN information</li>
                      <li>• EHCP (Education, Health and Care Plan) integration</li>
                      <li>• Student search and filtering capabilities</li>
                      <li>• Progress history and achievement tracking</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.BookOpen className="w-5 h-5 mr-2" /> Curriculum Generation</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• AI-powered curriculum creation based on student needs</li>
                      <li>• Multiple assessment frameworks (Engagement Model, etc.)</li>
                      <li>• Personalized learning objectives and activities</li>
                      <li>• Curriculum saving and management</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Tracking</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Real-time progress monitoring dashboard</li>
                      <li>• Achievement tracking across multiple areas</li>
                      <li>• Progress reports and analytics</li>
                      <li>• Student performance insights</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-orange-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Intervention System</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Targeted intervention planning and tracking</li>
                      <li>• Session logging and attendance monitoring</li>
                      <li>• Progress evaluation and adjustment</li>
                      <li>• Multi-student intervention management</li>
                    </ul>
                  </div>

                  <div className="border-l-4 border-indigo-500 pl-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center"><Icons.Calendar className="w-5 h-5 mr-2" /> Term Planning</h3>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Academic term planning and organization</li>
                      <li>• Learning objective mapping</li>
                      <li>• Resource allocation and scheduling</li>
                      <li>• Term-based progress assessment</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'navigation' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Navigation Guide</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Main Navigation</h3>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.Home className="w-5 h-5 mr-2" /> Dashboard</h4>
                      <p className="text-sm text-gray-600">Central hub with quick access to all features</p>
                      <Link to="/dashboard" className="text-blue-600 text-sm hover:underline">Go to Dashboard →</Link>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.Users className="w-5 h-5 mr-2" /> Students</h4>
                      <p className="text-sm text-gray-600">Manage student profiles and records</p>
                      <Link to="/student-organization" className="text-blue-600 text-sm hover:underline">Go to Students →</Link>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.BookOpen className="w-5 h-5 mr-2" /> Generate Curriculum</h4>
                      <p className="text-sm text-gray-600">Create personalized curricula for students</p>
                      <Link to="/generate-curriculum" className="text-blue-600 text-sm hover:underline">Go to Curriculum →</Link>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900">Additional Features</h3>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.Calendar className="w-5 h-5 mr-2" /> Term Plans</h4>
                      <p className="text-sm text-gray-600">Create and manage academic term plans</p>
                      <Link to="/generate-term-plan" className="text-blue-600 text-sm hover:underline">Go to Term Plans →</Link>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Interventions</h4>
                      <p className="text-sm text-gray-600">Track student interventions and progress</p>
                      <Link to="/interventions" className="text-blue-600 text-sm hover:underline">Go to Interventions →</Link>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center"><Icons.ChartBar className="w-5 h-5 mr-2" /> Progress Dashboard</h4>
                      <p className="text-sm text-gray-600">Monitor overall student progress</p>
                      <Link to="/progress-dashboard" className="text-blue-600 text-sm hover:underline">Go to Progress →</Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'getting-started' && (
            <div className="px-6 space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Getting Started</h2>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">Step 1: Add Students</h3>
                    <ol className="text-blue-800 space-y-1 ml-4">
                      <li>1. Navigate to the Students page</li>
                      <li>2. Click "Add New Student"</li>
                      <li>3. Fill in student details and SEN information</li>
                      <li>4. Save the student profile</li>
                    </ol>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-green-900 mb-2">Step 2: Generate Curriculum</h3>
                    <ol className="text-green-800 space-y-1 ml-4">
                      <li>1. Go to Generate Curriculum page</li>
                      <li>2. Select the student(s) you want to create curricula for</li>
                      <li>3. Choose assessment framework and parameters</li>
                      <li>4. Generate and review the curriculum</li>
                      <li>5. Save to your system</li>
                    </ol>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-2">Step 3: Monitor Progress</h3>
                    <ol className="text-purple-800 space-y-1 ml-4">
                      <li>1. Use the Progress Dashboard to track student development</li>
                      <li>2. Set up interventions for students needing support</li>
                      <li>3. Log sessions and track achievements</li>
                      <li>4. Generate progress reports as needed</li>
                    </ol>
                  </div>

                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-orange-900 mb-2">Step 4: Plan and Organize</h3>
                    <ol className="text-orange-800 space-y-1 ml-4">
                      <li>1. Create term plans to organize learning objectives</li>
                      <li>2. Schedule regular progress reviews</li>
                      <li>3. Adjust curricula based on student progress</li>
                      <li>4. Collaborate with other staff members</li>
                    </ol>
                  </div>
                </div>

                <div className="mt-8 bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2 flex items-center"><Icons.LightBulb className="w-5 h-5 mr-2" /> Pro Tips:</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Start with a few students to familiarize yourself with the system</li>
                    <li>• Use the help pages for each feature to learn more</li>
                    <li>• Save your work regularly to avoid losing progress</li>
                    <li>• Explore the different assessment frameworks available</li>
                    <li>• Set up regular reminders for progress reviews</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default HelpDashboardPage; 